import * as FieldService from './FieldService'
import * as LandService from './LandService'
import * as GapService from './GapService'
import {buildFieldLandDownloadSubject} from '../ViewField/ViewLand/Layers/downloadArchive'
import {isPlanetHdDateAllowed} from '../ViewField/ViewLand/Images/planetHdAcquisitionQuery'

const normalizeArray = (value) => {
  return Array.isArray(value) ? value : []
}

const sortByName = (items = []) => {
  return [...items].sort((a, b) => {
    const nameA = (a?.name || '').toLowerCase()
    const nameB = (b?.name || '').toLowerCase()

    return nameA.localeCompare(nameB, 'en', {numeric: true})
  })
}

// V2 lists use polygon; V1 detail/image consumers use polygons. Keep both and
// preserve every provider field while exposing one geometry to those consumers.
const normalizeLandGeometry = (land) => (
  land?.polygons == null && land?.polygon != null
    ? {...land, polygons: land.polygon}
    : land
)

const normalizeFieldList = (payload) => {
  return sortByName(payload?.fields || payload?.data || [])
}

const normalizeFieldLands = (payload) => {
  const lands = Array.isArray(payload)
    ? payload
    : payload?.lands ||
      payload?.data?.lands ||
      payload?.data?.data ||
      payload?.data ||
      []

  return sortByName(normalizeArray(lands).map(normalizeLandGeometry))
}

const normalizeFieldGaps = (payload) => {
  return normalizeArray(payload?.gaps)
}

const normalizeLandDetail = (payload) => {
  return normalizeLandGeometry(
    payload?.data?.land || payload?.data?.data || payload?.land || payload?.data || payload || null
  )
}

const getDateValue = (value) => {
  if (typeof value === 'string') {
    return value
  }

  return value?.date || value?.value || value?.datetime || null
}

const normalizeDateValues = (values) =>
  normalizeArray(values).map(getDateValue).filter(Boolean)

const normalizeLandDates = (payload) => {
  const data = payload?.data || payload || {}
  const source = Array.isArray(data) ? data : data.dates || data.results

  return normalizeDateValues(source)
}

const normalizeHdAcquisitions = (payload) => {
  const data = payload?.data || payload || {}
  return normalizeArray(
    Array.isArray(data)
      ? data
      : data.acquisitions || data.results || data.items
  ).filter((item) => isPlanetHdDateAllowed(item?.date || item?.acquired_at))
}

const normalizeAmbientDetail = (payload) => {
  const data = payload?.data || payload || null
  if (!data || typeof data !== 'object') {
    return data
  }

  const classCount = data.params?.breaks
    ?? data.params?.num_classes
    ?? data.count_ambients
    ?? data.num_classes
    ?? (Array.isArray(data.legend) ? data.legend.length : null)
    ?? (Array.isArray(data.breaks) ? data.breaks.length : null)
  const params = {
    ...(data.params || {}),
    ...(classCount != null && data.params?.breaks == null
      ? {breaks: classCount, num_classes: classCount}
      : {}),
    ...(data.month_range && !data.params?.month_range
      ? {month_range: data.month_range}
      : {}),
    ...(data.years_list && !data.params?.years
      ? {years: data.years_list}
      : {}),
    ...(data.basis && !data.params?.basis ? {basis: data.basis} : {}),
    ...(data.legend && !data.params?.legend ? {legend: data.legend} : {}),
    ...((data.layer_date || data.acquired_at || data.date) && !data.params?.layer_date
      ? {layer_date: data.layer_date || data.acquired_at || data.date}
      : {}),
  }

  return {
    ...data,
    ambient_id:
      data.ambient_id || data.ambient_hd_id || data.image_ambient_hd_id || null,
    legend: data.legend || data.params?.legend || null,
    params,
  }
}

const normalizeHdAccess = (payload) => {
  const data = payload?.data || payload || null
  if (!data || typeof data !== 'object') {
    return data
  }

  const access = data.access && typeof data.access === 'object'
    ? data.access
    : null

  return access
    ? {...data, ...access, access}
    : data
}

const normalizePrescriptionClassification = (payload) => {
  const data = payload?.data || payload || {}
  const classes = Array.isArray(data?.classes)
    ? data.classes
    : Array.isArray(data?.data)
      ? data.data
      : data?.data && typeof data.data === 'object'
        ? Object.values(data.data)
        : []

  return {
    classes,
    preview: data?.preview || {type: 'FeatureCollection', features: []},
    boundingBox: data?.bounding_box || [],
  }
}

const normalizeSearchLands = (lands = []) => {
  return normalizeArray(lands).map((land) => {
    const fieldId = land?.field?.field_id || land?.field_id
    const fieldName = land?.field?.name || land?.field_name || ''
    const landId = land?.land_id || land?.id
    const label =
      buildFieldLandDownloadSubject({fieldName, landName: land?.name || land?.label}) ||
      land?.label ||
      ''

    return normalizeLandGeometry({
      ...land,
      id: landId,
      land_id: landId,
      field_id: fieldId,
      label,
    })
  })
}

const groupSearchLands = (lands = []) => {
  const groups = {}

  normalizeSearchLands(lands).forEach((land) => {
    if (!land.field_id) {
      return
    }

    if (!groups[land.field_id]) {
      groups[land.field_id] = {
        name: land?.field?.name || land?.field_name || '',
        field_id: land.field_id,
        lands: [],
      }
    }

    groups[land.field_id].lands.push({
      ...land,
      field: land.field || {
        field_id: land.field_id,
        name: groups[land.field_id].name,
      },
      id: land.land_id,
      land_id: land.land_id,
      name: land.name,
      ha: land.ha,
      polygon: land.polygon,
      polygons: land.polygons,
    })
  })

  return Object.values(groups)
}

export const listFields = async () => {
  const res = await FieldService.list()

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeFieldList(res.data),
  }
}

export const getField = async (fieldId) => {
  const res = await FieldService.get(fieldId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: res.data,
  }
}

export const listFieldLands = async (fieldId) => {
  const res = await LandService.list(fieldId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeFieldLands(res.data),
  }
}

export const listFieldGaps = async (fieldId) => {
  const res = await GapService.list(fieldId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeFieldGaps(res.data),
  }
}

export const getFieldBundle = async (fieldId) => {
  const [fieldRes, landsRes, gapsRes] = await Promise.all([
    getField(fieldId),
    listFieldLands(fieldId),
    listFieldGaps(fieldId),
  ])

  if (!fieldRes?.ok) {
    return fieldRes
  }

  if (!landsRes?.ok) {
    return landsRes
  }

  if (!gapsRes?.ok) {
    return gapsRes
  }

  return {
    ok: true,
    data: {
      field: fieldRes.data,
      lands: landsRes.data,
      gaps: gapsRes.data,
    },
  }
}

export const listSearchIndex = async (filters) => {
  const res = await LandService.all(filters)

  if (!res?.ok) {
    return res
  }

  const lands = normalizeSearchLands(res.data?.data)

  return {
    ok: true,
    data: {
      lands,
      options: lands.map((land) => ({
        id: land.land_id,
        field_id: land.field_id,
        label: land.label,
      })),
      groupedOptions: groupSearchLands(lands),
    },
  }
}

export const getLand = async (landId, requestOptions = {}) => {
  const res = await LandService.get(landId, requestOptions)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeLandDetail(res.data),
  }
}

export const getLandDates = async (landId, filters) => {
  const res = await LandService.datesV2(landId, filters)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeLandDates(res.data),
  }
}

export const getLandAmbient = async (landId) => {
  const res = await LandService.getAmbient(landId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const getLandAmbientHd = async (landId) => {
  const res = await LandService.getAmbientHd(landId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const getLandImageAmbient = async (landId) => {
  const res = await LandService.getImageAmbient(landId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const createLandAmbient = async (landId, data) => {
  const res = await LandService.createAmbient(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const createLandAmbientHd = async (landId, data) => {
  const res = await LandService.createAmbientHd(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const updateLandAmbient = async (landId, data) => {
  const res = await LandService.updateAmbient(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const updateLandAmbientHd = async (landId, data) => {
  const res = await LandService.updateAmbientHd(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const createLandImageAmbient = async (landId, data) => {
  const res = await LandService.createImageAmbient(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const getLandImageAmbientHd = async (landId) => {
  const res = await LandService.getImageAmbientHd(landId)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const createLandImageAmbientHd = async (landId, data) => {
  const res = await LandService.createImageAmbientHd(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeAmbientDetail(res.data),
  }
}

export const updateLand = async (landId, data) => {
  const res = await LandService.updateV2(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeLandDetail(res.data),
  }
}

export const deleteLand = (landId) => {
  return LandService.deleteLand(landId)
}

export const deleteLandAmbient = (landId) => {
  return LandService.deleteAmbient(landId)
}

export const deleteLandAmbientHd = (landId, data) => {
  return LandService.deleteAmbientHd(landId, data)
}

export const deleteLandImageAmbient = (landId) => {
  return LandService.deleteImageAmbient(landId)
}

export const deleteLandImageAmbientHd = (landId, data) => {
  return LandService.deleteImageAmbientHd(landId, data)
}

export const getLandHdAccess = async (landId, options) => {
  const res = await LandService.getHdAccess(landId, options)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeHdAccess(res.data),
  }
}

export const preflightLandHdAccess = async (landId, data) => {
  const res = await LandService.preflightHdAccess(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeHdAccess(res.data),
  }
}

export const confirmLandHdAccess = async (landId, data) => {
  const res = await LandService.confirmHdAccess(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeHdAccess(res.data),
  }
}

export const searchLandHdAcquisitions = async (landId, data) => {
  const res = await LandService.searchHdAcquisitions(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizeHdAcquisitions(res.data),
  }
}

export const prepareLandHdAcquisition = async (landId, acquisitionId, data) => {
  const res = await LandService.prepareHdAcquisition(landId, acquisitionId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: res.data?.data || res.data,
  }
}

export const getLandHdAcquisitionLayer = (
  landId,
  acquisitionId,
  layer,
  options,
  cancelTokenRequest
) => LandService.getHdAcquisitionLayer(
  landId,
  acquisitionId,
  layer,
  options,
  cancelTokenRequest
)

export const classifyLandPrescription = async (landId, data) => {
  const res = await LandService.classifyPrescription(landId, data)

  if (!res?.ok) {
    return res
  }

  return {
    ok: true,
    data: normalizePrescriptionClassification(res.data),
  }
}

export const downloadLandLayer = (landId, params) => {
  return LandService.downloadLayer(landId, params)
}

export const downloadLandAmbient = (landId, params) => {
  return LandService.downloadAmbient(landId, params)
}

export const downloadLandImageAmbient = (landId, params) => {
  return LandService.downloadImageAmbient(landId, params)
}

export const downloadLandAmbientHd = (landId, params) => {
  return LandService.downloadAmbientHd(landId, params)
}

export const downloadLandImageAmbientHd = (landId, params) => {
  return LandService.downloadImageAmbientHd(landId, params)
}

export const listLandYieldMaps = (landId) => {
  return LandService.listYieldMaps(landId)
}

export const getLandSoilMap = (landId) => {
  return LandService.getSoilMap(landId)
}

export const downloadLandSoilMap = (landId, params) => {
  return LandService.downloadSoilMap(landId, params)
}

export const downloadLandYieldMap = (landId, yieldMapId, params) => {
  return LandService.downloadYieldMap(landId, yieldMapId, params)
}

export const downloadLandPrescription = ({landId, data}) => {
  return LandService.downloadPrescription({land_id: landId, data})
}

export const downloadLandPrescriptionAmbient = ({landId, data}) => {
  return LandService.downloadPrescriptionAmbient({land_id: landId, data})
}

export const downloadLandPrescriptionImageAmbient = ({landId, data}) => {
  return LandService.downloadPrescriptionImageAmbient({land_id: landId, data})
}

export const downloadLandPrescriptionAmbientHd = ({landId, data}) => {
  return LandService.downloadPrescriptionAmbientHd({land_id: landId, data})
}

export const downloadLandPrescriptionImageAmbientHd = ({landId, data}) => {
  return LandService.downloadPrescriptionImageAmbientHd({land_id: landId, data})
}
